import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { RoleInput } from "~/operations";
import { LoadingButton } from "~/components/loading-button";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  hasAnyBaseRoleSelected,
  Member,
} from "~/components/Members/utils/membersUtils";
import { Option } from "~/components/Form/components/SelectMenu/SelectMenu";
import { useState } from "react";
import { SelectMenu } from "~/components/Form/components/SelectMenu/SelectMenu";
import { MembersRolesList } from "~/components/Members/MembersRolesList";
import {
  BASE_MEMBERS_ROLES,
  GROUPED_MEMBERS_ROLES,
  PERMISSIONS,
} from "~/components/Members/const";
import { roleOrgMember } from "~/lib/iam/roles/OrgMember";

type FormValues = {
  selectedRoles: string[];
};

export type EditMemberRoleDialogProps = {
  open: boolean;
  member: Member;
  onCancel: () => void;
  onSave: (roles: RoleInput[]) => void;
  onDelete?: () => void;
  isLoading: boolean;
};

export function EditMemberRoleDialog({
  member,
  open,
  onCancel,
  onSave,
  onDelete,
  isLoading,
}: EditMemberRoleDialogProps) {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      selectedRoles: member.roles
        .map((role) => role.mrn)
        .filter((mrn) => mrn !== roleOrgMember.mrn),
    },
    mode: "onChange",
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [isRolesDropdownOpen, setIsRolesDropdownOpen] = useState(false);
  const watchSelectedRoles = watch("selectedRoles");

  const onSubmit = (data: FormValues) => {
    onSave(data.selectedRoles.map((role) => ({ mrn: role })));
  };

  return (
    <Dialog
      id="edit-member-dialog"
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: "background.default",
            position: "relative",
            overflow: "visible",
          },
        },
      }}
    >
      <DialogTitle
        id="edit-member-dialog-title"
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 5,
          pb: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PersonIcon sx={{ mr: 1.5 }} />
          <Typography
            variant="h5"
            sx={{
              fontWeight: "700",
              textTransform: "uppercase",
            }}
          >
            Edit Member
          </Typography>
        </Box>
        <IconButton onClick={onCancel} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 5, overflow: "visible" }}>
        <Grid container alignItems="center">
          <Grid size={12} mt={3}>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: 24 }}>
              {member.identity?.name}
            </Typography>
          </Grid>
          <Grid size={12} mt={3}>
            <Button
              variant="text"
              disabled={false}
              color="primary"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                setAnchorEl(event.currentTarget);
                setIsRolesDropdownOpen(true);
              }}
              startIcon={<AddIcon />}
              data-testid="add-roles-button"
            >
              Add Roles
            </Button>

            <FormHelperText error>
              {errors.selectedRoles?.message || ""}
            </FormHelperText>
          </Grid>

          <Controller
            name="selectedRoles"
            control={control}
            rules={{
              validate: (value) => {
                const isHasAnyBaseRoleSelected = hasAnyBaseRoleSelected(value);
                if (!isHasAnyBaseRoleSelected) {
                  return "Must have at least one base role selected";
                }
                return true;
              },
            }}
            render={({ field, formState: { errors } }) => (
              <Box sx={{ position: "relative" }}>
                <SelectMenu
                  isOpen={isRolesDropdownOpen}
                  anchorRef={anchorEl}
                  onClose={() => {
                    setAnchorEl(null);
                    setIsRolesDropdownOpen(false);
                  }}
                  maxHeight={208}
                  options={GROUPED_MEMBERS_ROLES}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  multiple={true}
                  value={field.value}
                  variant="checkbox"
                  error={!!errors.selectedRoles}
                  helperText={errors.selectedRoles?.message || ""}
                />
              </Box>
            )}
          />

          <Box pl={1} mt={3}>
            <MembersRolesList
              roles={watchSelectedRoles
                .map((roleMrn) => {
                  const label = [...BASE_MEMBERS_ROLES, ...PERMISSIONS].find(
                    (role) => role.value === roleMrn,
                  )?.label;

                  return label ? { value: roleMrn, label } : null;
                })
                .filter((option): option is Option => option !== null)}
              onDelete={(roleMrn) => {
                setValue(
                  "selectedRoles",
                  watchSelectedRoles.filter((x) => x !== roleMrn),
                  { shouldValidate: true, shouldDirty: true },
                );
              }}
            />
          </Box>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          position: "sticky",
          justifyContent: "center",
          alignItems: "start",
          bottom: 0,
          left: 0,
          height: (theme) => theme.spacing(10.5),
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: onDelete ? "space-between" : "flex-end",
            width: "100%",
            px: 4.25,
          }}
        >
          {onDelete && (
            <Button
              color="error"
              variant="outlined"
              onClick={onDelete}
              disabled={isLoading}
              sx={{ mr: "auto" }}
            >
              Remove Member
            </Button>
          )}
          <LoadingButton
            loading={isLoading}
            buttonText="Apply"
            variant="contained"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading || !isDirty || !isValid}
            data-name="save-role-button"
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}
